import { BaseBrandService, BrandCreateInput, BrandUpdateInput, BrandQueryOptions, BrandServiceResponse } from './brandService'
import { ParsedBrand } from '../types/brand'

// D1 Database configuration
interface D1Database {
  prepare(query: string): D1PreparedStatement
  exec(query: string): Promise<D1ExecResult>
  batch(statements: D1PreparedStatement[]): Promise<D1BatchResult[]>
}

interface D1PreparedStatement {
  bind(...values: any[]): D1PreparedStatement
  first<T = any>(): Promise<T | null>
  all<T = any>(): Promise<{ results: T[]; meta: any }>
  run(): Promise<{ success: boolean; meta: any }>
}

interface D1ExecResult {
  success: boolean
  error?: string
  meta: any
}

interface D1BatchResult {
  success: boolean
  error?: string
  results: any[]
  meta: any
}

export class D1BrandService extends BaseBrandService {
  private db: D1Database
  
  constructor(database: D1Database) {
    super()
    this.db = database
  }
  
  async getAllBrands(options?: BrandQueryOptions): Promise<BrandServiceResponse<ParsedBrand[]>> {
    try {
      let query = `
        SELECT * FROM brands
        WHERE 1=1
      `
      const params: any[] = []
      
      // Add search filter
      if (options?.search) {
        query += ` AND (
          brand LIKE ? OR 
          consumer_summary LIKE ? OR 
          primary_products LIKE ? OR
          clean_categories LIKE ?
        )`
        const searchParam = `%${options.search}%`
        params.push(searchParam, searchParam, searchParam, searchParam)
      }
      
      // Add custom filters
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              const placeholders = value.map(() => '?').join(',')
              query += ` AND ${key} IN (${placeholders})`
              params.push(...value)
            } else {
              query += ` AND ${key} = ?`
              params.push(value)
            }
          }
        })
      }
      
      // Add ordering
      query += ` ORDER BY brand ASC`
      
      // Add pagination
      if (options?.limit !== undefined) {
        query += ` LIMIT ?`
        params.push(options.limit)
      }
      if (options?.offset !== undefined) {
        query += ` OFFSET ?`
        params.push(options.offset)
      }
      
      const stmt = this.db.prepare(query).bind(...params)
      const result = await stmt.all<any>()
      
      // Convert D1 results to ParsedBrand format
      const brands = result.results.map(this.convertD1RowToParsedBrand)
      
      return {
        data: brands,
        success: true,
        total: brands.length
      }
    } catch (error) {
      return {
        data: [],
        success: false,
        error: `Failed to get brands from D1: ${error}`
      }
    }
  }
  
  async getBrandByName(brandName: string): Promise<BrandServiceResponse<ParsedBrand | null>> {
    try {
      const stmt = this.db.prepare('SELECT * FROM brands WHERE brand = ?').bind(brandName)
      const result = await stmt.first<any>()
      
      const brand = result ? this.convertD1RowToParsedBrand(result) : null
      
      return {
        data: brand,
        success: true
      }
    } catch (error) {
      return {
        data: null,
        success: false,
        error: `Failed to get brand from D1: ${error}`
      }
    }
  }
  
  async createBrand(brand: BrandCreateInput): Promise<BrandServiceResponse<ParsedBrand>> {
    try {
      // Validate brand data
      const validation = await this.validateBrand(brand)
      if (!validation.success) {
        return {
          data: this.convertInputToParsedBrand(brand),
          success: false,
          error: validation.error
        }
      }
      
      const stmt = this.db.prepare(`
        INSERT INTO brands (
          brand, website, amazon_presence, manufacturing_verification,
          hq_location, mfg_locations, primary_products, product_categories,
          clean_categories, purchase_methods, founded_year, business_type,
          certifications, price_tier, consumer_summary, evidence_sources,
          confidence_score, last_verified
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `).bind(
        brand.brand,
        brand.website,
        brand.amazon_presence,
        brand.manufacturing_verification,
        brand.hq_location,
        brand.mfg_locations,
        brand.primary_products,
        brand.product_categories,
        brand.clean_categories,
        brand.purchase_methods,
        brand.founded_year,
        brand.business_type,
        brand.certifications,
        brand.price_tier,
        brand.consumer_summary,
        brand.evidence_sources,
        brand.confidence_score,
        brand.last_verified || new Date().toISOString()
      )
      
      const result = await stmt.run()
      
      if (!result.success) {
        return {
          data: this.convertInputToParsedBrand(brand),
          success: false,
          error: 'Failed to insert brand into D1'
        }
      }
      
      // Fetch the created brand
      const createdBrandResult = await this.getBrandByName(brand.brand)
      
      if (!createdBrandResult.data) {
        return {
          data: this.convertInputToParsedBrand(brand),
          success: false,
          error: 'Failed to retrieve created brand'
        }
      }
      
      return {
        data: createdBrandResult.data,
        success: createdBrandResult.success,
        error: createdBrandResult.error
      }
    } catch (error) {
      return {
        data: this.convertInputToParsedBrand(brand),
        success: false,
        error: `Failed to create brand in D1: ${error}`
      }
    }
  }
  
  async updateBrand(brandName: string, updates: BrandUpdateInput): Promise<BrandServiceResponse<ParsedBrand>> {
    try {
      // Get current brand data
      const currentResult = await this.getBrandByName(brandName)
      if (!currentResult.success || !currentResult.data) {
        return {
          data: this.convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
          success: false,
          error: `Brand with name '${brandName}' not found`
        }
      }
      
      // Build update query dynamically based on provided fields
      const updateFields: string[] = []
      const params: any[] = []
      
      Object.entries(updates).forEach(([key, value]) => {
        if (key !== 'brand' && value !== undefined) {
          updateFields.push(`${key} = ?`)
          params.push(value)
        }
      })
      
      if (updateFields.length === 0) {
        return {
          data: currentResult.data!,
          success: true,
          error: undefined
        } // No updates to apply
      }
      
      // Always update last_verified
      updateFields.push('last_verified = ?')
      params.push(new Date().toISOString())
      
      // Add WHERE clause parameter
      params.push(brandName)
      
      const stmt = this.db.prepare(`
        UPDATE brands 
        SET ${updateFields.join(', ')}
        WHERE brand = ?
      `).bind(...params)
      
      const result = await stmt.run()
      
      if (!result.success) {
        return {
          data: currentResult.data,
          success: false,
          error: 'Failed to update brand in D1'
        }
      }
      
      // Fetch the updated brand
      const updatedBrandResult = await this.getBrandByName(updates.brand || brandName)
      
      if (!updatedBrandResult.data) {
        return {
          data: this.convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
          success: false,
          error: 'Failed to retrieve updated brand'
        }
      }
      
      return {
        data: updatedBrandResult.data,
        success: updatedBrandResult.success,
        error: updatedBrandResult.error
      }
    } catch (error) {
      return {
        data: this.convertInputToParsedBrand({ ...updates, brand: brandName } as BrandCreateInput),
        success: false,
        error: `Failed to update brand in D1: ${error}`
      }
    }
  }
  
  async deleteBrand(brandName: string): Promise<BrandServiceResponse<boolean>> {
    try {
      const stmt = this.db.prepare('DELETE FROM brands WHERE brand = ?').bind(brandName)
      const result = await stmt.run()
      
      return {
        data: result.success,
        success: result.success,
        error: result.success ? undefined : 'Failed to delete brand from D1'
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `Failed to delete brand from D1: ${error}`
      }
    }
  }
  
  async refreshData(): Promise<BrandServiceResponse<boolean>> {
    // For D1, refreshing might involve cache invalidation or reloading from external source
    try {
      // This is a placeholder - implement based on your data refresh strategy
      return {
        data: true,
        success: true
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `Failed to refresh D1 data: ${error}`
      }
    }
  }
  
  // Utility methods for D1-specific operations
  async initializeDatabase(): Promise<BrandServiceResponse<boolean>> {
    try {
      const createTableQuery = `
        CREATE TABLE IF NOT EXISTS brands (
          brand TEXT PRIMARY KEY,
          website TEXT,
          amazon_presence TEXT,
          manufacturing_verification TEXT,
          hq_location TEXT,
          mfg_locations TEXT,
          primary_products TEXT,
          product_categories TEXT,
          clean_categories TEXT,
          purchase_methods TEXT,
          founded_year TEXT,
          business_type TEXT,
          certifications TEXT,
          price_tier TEXT,
          consumer_summary TEXT,
          evidence_sources TEXT,
          confidence_score INTEGER,
          last_verified TEXT,
          created_at DATETIME DEFAULT CURRENT_TIMESTAMP,
          updated_at DATETIME DEFAULT CURRENT_TIMESTAMP
        )
      `
      
      const result = await this.db.exec(createTableQuery)
      
      return {
        data: result.success,
        success: result.success,
        error: result.success ? undefined : 'Failed to initialize D1 database'
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `Failed to initialize D1 database: ${error}`
      }
    }
  }
  
  async migrationFromCSV(csvBrands: ParsedBrand[]): Promise<BrandServiceResponse<boolean>> {
    try {
      const statements: D1PreparedStatement[] = []
      
      csvBrands.forEach(brand => {
        const stmt = this.db.prepare(`
          INSERT OR REPLACE INTO brands (
            brand, website, amazon_presence, manufacturing_verification,
            hq_location, mfg_locations, primary_products, product_categories,
            clean_categories, purchase_methods, founded_year, business_type,
            certifications, price_tier, consumer_summary, evidence_sources,
            confidence_score, last_verified
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        `).bind(
          brand.brand,
          brand.website,
          brand.amazon_presence,
          brand.manufacturing_verification,
          brand.hq_location,
          brand.mfg_locations.join(';'),
          brand.primary_products,
          brand.product_categories.join(';'),
          brand.clean_categories.join(','),
          brand.purchase_methods,
          brand.founded_year,
          brand.business_type,
          brand.certifications.join(';'),
          brand.price_tier,
          brand.consumer_summary,
          brand.evidence_sources,
          brand.confidence_score,
          brand.last_verified
        )
        
        statements.push(stmt)
      })
      
      const results = await this.db.batch(statements)
      const failedCount = results.filter(r => !r.success).length
      
      return {
        data: failedCount === 0,
        success: failedCount === 0,
        error: failedCount > 0 ? `${failedCount} brands failed to migrate` : undefined,
        total: csvBrands.length - failedCount
      }
    } catch (error) {
      return {
        data: false,
        success: false,
        error: `CSV migration failed: ${error}`
      }
    }
  }
  
  private convertD1RowToParsedBrand(row: any): ParsedBrand {
    return {
      brand: row.brand,
      website: row.website,
      amazon_presence: row.amazon_presence,
      manufacturing_verification: row.manufacturing_verification,
      hq_location: row.hq_location,
      mfg_locations: row.mfg_locations ? row.mfg_locations.split(';').map((s: string) => s.trim()) : [],
      primary_products: row.primary_products,
      product_categories: row.product_categories ? row.product_categories.split(';').map((s: string) => s.trim()) : [],
      clean_categories: row.clean_categories ? row.clean_categories.split(',').map((s: string) => s.trim()) : [],
      purchase_methods: row.purchase_methods,
      founded_year: row.founded_year === 'unknown' ? 'unknown' : parseInt(row.founded_year, 10),
      business_type: row.business_type,
      certifications: row.certifications ? row.certifications.split(';').map((s: string) => s.trim()) : [],
      price_tier: row.price_tier,
      consumer_summary: row.consumer_summary,
      evidence_sources: row.evidence_sources,
      confidence_score: row.confidence_score,
      last_verified: row.last_verified,
      hq_state: this.extractStateFromLocation(row.hq_location),
      founded_decade: typeof row.founded_year === 'number' ? Math.floor(row.founded_year / 10) * 10 : undefined
    }
  }
  
  private convertInputToParsedBrand(input: BrandCreateInput): ParsedBrand {
    // This is a simplified conversion - in real implementation you'd want proper validation
    return {
      brand: input.brand,
      website: input.website,
      amazon_presence: input.amazon_presence,
      manufacturing_verification: input.manufacturing_verification,
      hq_location: input.hq_location,
      mfg_locations: input.mfg_locations ? input.mfg_locations.split(';').map(s => s.trim()) : [],
      primary_products: input.primary_products,
      product_categories: input.product_categories ? input.product_categories.split(';').map(s => s.trim()) : [],
      clean_categories: input.clean_categories ? input.clean_categories.split(',').map(s => s.trim()) : [],
      purchase_methods: input.purchase_methods,
      founded_year: input.founded_year,
      business_type: input.business_type,
      certifications: input.certifications ? input.certifications.split(';').map(s => s.trim()) : [],
      price_tier: input.price_tier,
      consumer_summary: input.consumer_summary,
      evidence_sources: input.evidence_sources,
      confidence_score: input.confidence_score,
      last_verified: input.last_verified || new Date().toISOString(),
      hq_state: this.extractStateFromLocation(input.hq_location),
      founded_decade: typeof input.founded_year === 'number' ? Math.floor(input.founded_year / 10) * 10 : undefined
    }
  }
  
  private extractStateFromLocation(location: string): string | undefined {
    const US_STATES = [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
      'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
      'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
      'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
      'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
      'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
      'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
    ]
    
    if (!location || location === 'unknown') return undefined
    
    const foundState = US_STATES.find(state => 
      location.toLowerCase().includes(state.toLowerCase())
    )
    
    return foundState
  }
}